import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActiveInvestigationsTable = ({ investigations = [] }) => {
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'paused':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'completed':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'archived':
        return 'bg-text-secondary/10 text-text-secondary border-text-secondary/20';
      default:
        return 'bg-muted text-text-secondary border-border';
    }
  };

  const getPriorityColor = (priority) => {
    if (priority >= 4) return 'bg-error/10 text-error border-error/20';
    if (priority >= 3) return 'bg-warning/10 text-warning border-warning/20';
    return 'bg-success/10 text-success border-success/20';
  };

  const getPriorityLabel = (priority) => {
    if (priority >= 4) return 'High';
    if (priority >= 3) return 'Medium';
    return 'Low';
  };

  const handleViewInvestigation = (investigation) => {
    navigate('/transaction-analysis', {
      state: {
        investigation,
        addresses: investigation?.target_addresses || []
      }
    });
  };

  if (investigations?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-forensic-card p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Search" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-text-primary">Active Investigations</h3>
        </div>
        
        <div className="text-center py-12">
          <Icon name="FolderOpen" size={48} color="var(--color-text-secondary)" className="mx-auto mb-4" />
          <p className="text-text-secondary text-sm">No active investigations found</p>
          <p className="text-text-secondary text-xs mt-1">Start a new investigation to begin tracking cryptocurrency activities</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-forensic-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Search" size={20} color="var(--color-primary)" />
            <h3 className="text-lg font-semibold text-text-primary">Active Investigations</h3>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/investigation-dashboard')}
            iconName="ExternalLink"
          >
            View All
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Case</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Title</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Investigator</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Status</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Priority</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Addresses</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Funds Traced</th>
              <th className="text-left p-4 text-sm font-medium text-text-secondary">Actions</th>
            </tr>
          </thead>
          <tbody>
            {investigations?.slice(0, 10)?.map((investigation, index) => (
              <tr key={investigation?.id || index} className="border-b border-border hover:bg-muted/50">
                <td className="p-4">
                  <div className="font-medium text-text-primary text-sm">
                    {investigation?.case_number}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {new Date(investigation?.created_at)?.toLocaleDateString()}
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm text-text-primary font-medium max-w-48 truncate">
                    {investigation?.title}
                  </div>
                  {investigation?.description && (
                    <div className="text-xs text-text-secondary max-w-48 truncate">
                      {investigation?.description}
                    </div>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="User" size={12} color="var(--color-primary)" />
                    </div>
                    <div className="text-sm text-text-primary">
                      {investigation?.investigator?.full_name || 'Unassigned'}
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(investigation?.status)}`}>
                    {investigation?.status || 'active'}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(investigation?.priority)}`}>
                    {getPriorityLabel(investigation?.priority)}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-1">
                    <Icon name="MapPin" size={14} color="var(--color-text-secondary)" />
                    <span className="text-sm text-text-primary">
                      {investigation?.investigation_addresses?.length || investigation?.target_addresses?.length || 0}
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm font-medium text-text-primary">
                    ${parseFloat(investigation?.total_funds_traced || 0)?.toFixed(2)}M
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleViewInvestigation(investigation)}
                      className="p-2"
                    >
                      <Icon name="Eye" size={14} color="currentColor" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate('/address-risk-assessment', {
                        state: {
                          investigation,
                          addresses: investigation?.target_addresses || []
                        }
                      })}
                      className="p-2"
                    >
                      <Icon name="Search" size={14} color="currentColor" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActiveInvestigationsTable;